<script setup lang="ts">
import type { Csv } from '@/apis/generated/dialbunny';
import { DATA_QA } from './csv-import-details-step.constants';
import { DtNotice } from '@dialpad/dialtone/vue3/lib/notice';
import { DtButton } from '@dialpad/dialtone/vue3/lib/button';
import { DtStack } from '@dialpad/dialtone/vue3/lib/stack';
import { DtIcon } from '@dialpad/dialtone/vue3/lib/icon';
import { nextTick, onMounted, provide, ref, watch } from 'vue';
import { useCsvStore } from '@/stores';
import { handlePatchErrorFactory } from '@/csv/utils/csv-handle-patch-error';
import { useI18N } from '@dialpad/i18n';
import CsvNameInput from '../CsvNameInput/CsvNameInput.vue';
import logger from '@/logger';
import { TRIGGER_FORM_VALIDATION } from '@/app/utils/keys';

const log = logger.getChild('CsvImportDetailsStep');

log.debug('Mounting');

onMounted(() => {
  log.debug('Mounted');
});

type Props = {
  csv: Csv;
};
const props = defineProps<Props>();

const name = ref('');

const validationTrigger = ref(0);
provide(TRIGGER_FORM_VALIDATION, validationTrigger);
const cvsNameError = ref<string | null>(null);

watch(
  () => props.csv.name,
  () => {
    name.value = props.csv.name ?? props.csv.filename ?? '';
  },
  { immediate: true },
);

const csvStore = useCsvStore();
const { $ta } = useI18N();

const emit = defineEmits(['back', 'continue']);

const loading = ref(false);

const handlePatchError = handlePatchErrorFactory($ta('CSV_IMPORT_PATCH_ERROR'));

const submit = async () => {
  validationTrigger.value++;
  await nextTick();

  if (cvsNameError.value) {
    log.debug('Name is invalid');
    return;
  }

  loading.value = true;
  log.debug('Updating csv record...');
  csvStore.update(
    props.csv.id,
    {
      action: 'import',
      name: name.value,
    },
    {
      onSuccess: async () => {
        log.debug('Update successful, refreshing store...');
        await csvStore.refresh();
        log.debug('Store refreshed, emitting continue');

        emit('continue');
        loading.value = false;
      },
      onRetry: () => {
        // show toast
      },
      onError: async (error) => {
        await handlePatchError(error);
        loading.value = false;
      },
    },
  );
};
</script>
<template>
  <div :data-qa="DATA_QA.ROOT">
    <p class="d-fc-secondary">
      {{ $t('CSV_IMPORT_DETAILS_RECORDS_TO_IMPORT') }}
    </p>
    <p
      :data-qa="DATA_QA.ROW_COUNT"
      class="d-fc-primary d-fs-500 d-fw-medium d-mt-16"
    >
      {{ csv?.numRows ?? 0 }}
    </p>
    <DtNotice :hide-close="true" class="d-wmx100p">
      <span>{{ $t('CSV_IMPORT_DETAILS_WARNING') }}</span>
      <template #icon>
        <DtIcon name="info" size="400" />
      </template>
    </DtNotice>
    <CsvNameInput
      v-model="name"
      :label="$t('CSV_IMPORT_CSV_NAME_LABEL')"
      class="d-mt16"
      @update:error="cvsNameError = $event"
    />
    <div class="d-d-flex d-jc-flex-end d-mt16">
      <DtStack direction="row" gap="400">
        <DtButton
          :data-qa="DATA_QA.BACK"
          importance="clear"
          @click="emit('back')"
        >
          {{ $t('CSV_IMPORT_BACK') }}
        </DtButton>
        <DtButton :data-qa="DATA_QA.NEXT" :loading="loading" @click="submit">
          {{ $t('CSV_IMPORT_IMPORT') }}
        </DtButton>
      </DtStack>
    </div>
  </div>
</template>
